import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebaseConfig';

interface AnswerSheet {
    id: string;
    TotalScore: number;
    Cp1Score: number;
    Cp2Score: number;
    Cp3Score: number;
    Cp4Score: number;
    Cp5Score: number;
    ShikakuName: string;
    ShikakuRef: any;
    CreateDate: any;
    [key: string]: any; // For ANSW01-ANSW50
}

interface Question {
    id: string;
    Chapter: number;
    Qno: number;
    SortNo?: number;
    Question: string;
    Answer01: string;
    Answer02: string;
    Answer03: string;
    CollectAnswer: string;
    Explanation?: string;
}

interface QuestionWithAnswer extends Question {
    userAnswer: string;
    isCorrect: boolean;
}

export default function ExamResultDetail() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [answerSheet, setAnswerSheet] = useState<AnswerSheet | null>(null);
    const [questions, setQuestions] = useState<QuestionWithAnswer[]>([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState(1);

    const [shikakuData, setShikakuData] = useState<any>(null);

    useEffect(() => {
        fetchResultDetail();
    }, [id]);

    const fetchResultDetail = async () => {
        if (!id) return;

        try {
            // Fetch AnswerSheet
            const answerSheetDoc = await getDoc(doc(db, 'AnswerSheet', id));
            if (!answerSheetDoc.exists()) {
                alert('試験結果が見つかりません');
                navigate('/results');
                return;
            }

            const answerSheetData = {
                id: answerSheetDoc.id,
                ...answerSheetDoc.data()
            } as AnswerSheet;
            setAnswerSheet(answerSheetData);

            // Fetch Shikaku Data for comments
            const shikakuRef = answerSheetData.ShikakuRef;
            if (shikakuRef) {
                try {
                    const shikakuDoc = await getDoc(shikakuRef);
                    if (shikakuDoc.exists()) {
                        setShikakuData(shikakuDoc.data());
                    }
                } catch (err) {
                    console.error("Error fetching shikaku data:", err);
                }
            }

            // Fetch Questions
            if (!shikakuRef) {
                setLoading(false);
                return;
            }

            const questionsQuery = query(
                collection(db, 'Questions'),
                where('SKref', '==', shikakuRef)
            );
            const questionsSnapshot = await getDocs(questionsQuery);

            const questionsData: QuestionWithAnswer[] = questionsSnapshot.docs
                .map((doc) => {
                    const data = doc.data() as Question;
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    const { id: _ignore, ...dataWithoutId } = data;

                    const questionIndex = ((data.Chapter - 1) * 10) + data.Qno;
                    const answerKey = `ANSW${String(questionIndex).padStart(2, '0')}`;
                    const userAnswer = answerSheetData[answerKey] || '';

                    return {
                        id: doc.id,
                        ...dataWithoutId,
                        userAnswer,
                        isCorrect: userAnswer === data.CollectAnswer
                    };
                })
                .sort((a, b) => {
                    if (a.Chapter !== b.Chapter) return a.Chapter - b.Chapter;
                    return a.Qno - b.Qno;
                });

            setQuestions(questionsData);
        } catch (error) {
            console.error('Error fetching result detail:', error);
            alert('データの取得に失敗しました');
        } finally {
            setLoading(false);
        }
    };

    const getChapterQuestions = (chapter: number) => {
        return questions.filter(q => q.Chapter === chapter);
    };

    const getScoreComment = () => {
        if (!answerSheet || !shikakuData) return null;
        const score = answerSheet.TotalScore;

        if (score === 100) return shikakuData.Lv100;
        if (score >= 95) return shikakuData.Lv95;
        if (score >= 90) return shikakuData.Lv90;
        if (score >= 80) return shikakuData.Lv80;
        if (score >= 70) return shikakuData.Lv70;
        return shikakuData.Lv1;
    };

    const scorePercentage = answerSheet ? (answerSheet.TotalScore / 100) * 100 : 0;
    const comment = getScoreComment();
    const radius = 60;
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset = circumference - (scorePercentage / 100) * circumference;

    if (loading) {
        return <div className="min-h-screen flex items-center justify-center">読み込み中...</div>;
    }

    if (!answerSheet) {
        return <div className="min-h-screen flex items-center justify-center">データが見つかりません</div>;
    }

    return (
        <div className="min-h-screen bg-[#F5F5F5]">
            <div className="max-w-md mx-auto px-4 py-6">
                {/* Back Button */}
                <div className="mb-6">
                    <button
                        onClick={() => navigate('/results')}
                        className="flex items-center gap-2 text-gray-500 hover:text-gray-700 transition-colors"
                    >
                        <i className="ri-arrow-left-s-line text-2xl"></i>
                        <span className="font-bold">結果一覧に戻る</span>
                    </button>
                </div>

                {/* Score Circle */}
                <div className="bg-white rounded-3xl shadow-sm p-6 mb-6">
                    <div className="flex flex-col items-center">
                        <h2 className="text-lg font-bold text-gray-800 mb-6">{answerSheet.ShikakuName}</h2>

                        {/* Circular Progress */}
                        <div className="relative w-40 h-40 mb-6">
                            <svg className="transform -rotate-90 w-40 h-40">
                                {/* Background circle */}
                                <circle
                                    cx="80"
                                    cy="80"
                                    r="60"
                                    stroke="#e5e7eb"
                                    strokeWidth="10"
                                    fill="none"
                                />
                                {/* Progress circle */}
                                <circle
                                    cx="80"
                                    cy="80"
                                    r="60"
                                    stroke="#F4B183"
                                    strokeWidth="10"
                                    fill="none"
                                    strokeDasharray={circumference}
                                    strokeDashoffset={strokeDashoffset}
                                    strokeLinecap="round"
                                    className="transition-all duration-1000"
                                />
                            </svg>
                            <div className="absolute inset-0 flex flex-col items-center justify-center">
                                <div className="text-3xl font-bold text-gray-800">{answerSheet.TotalScore}</div>
                                <div className="text-gray-500 text-xs">/ 100</div>
                            </div>
                        </div>

                        {/* Comment Section */}
                        {comment && (
                            <div className="w-full mb-6 bg-gray-50 border border-gray-200 rounded-xl p-4">
                                <h3 className="text-gray-800 font-bold mb-2 flex items-center gap-2 text-sm">
                                    <i className="ri-message-3-line"></i>
                                    総評
                                </h3>
                                <p className="text-gray-600 whitespace-pre-wrap leading-relaxed text-sm text-left">
                                    {comment}
                                </p>
                            </div>
                        )}

                        {/* Chapter Scores */}
                        <div className="grid grid-cols-5 gap-2 w-full">
                            {[1, 2, 3, 4, 5].map((chapter) => (
                                <div key={chapter} className="text-center">
                                    <div className="text-[10px] text-gray-500 mb-1">{chapter}章</div>
                                    <div className="text-sm font-bold text-gray-700">
                                        {answerSheet[`Cp${chapter}Score`] || 0}点
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Chapter Tabs */}
                <div className="bg-white rounded-3xl shadow-sm overflow-hidden">
                    <div className="flex border-b border-gray-100 overflow-x-auto">
                        {[1, 2, 3, 4, 5].map((chapter) => (
                            <button
                                key={chapter}
                                onClick={() => setActiveTab(chapter)}
                                className={`flex-1 min-w-[60px] py-3 font-bold transition-colors text-sm whitespace-nowrap ${activeTab === chapter
                                    ? 'text-[#F4B183] border-b-2 border-[#F4B183]'
                                    : 'text-gray-400 hover:bg-gray-50'
                                    }`}
                            >
                                {chapter}章
                            </button>
                        ))}
                    </div>

                    {/* Questions */}
                    <div className="p-4 space-y-4">
                        {getChapterQuestions(activeTab).map((question) => (
                            <div
                                key={question.id}
                                className={`rounded-2xl p-4 border ${question.isCorrect
                                    ? 'border-blue-100 bg-blue-50/30'
                                    : 'border-red-100 bg-red-50/30'
                                    }`}
                            >
                                {/* Question */}
                                <div className="mb-3">
                                    <h3 className="text-sm font-bold text-gray-800 leading-relaxed">
                                        <span className={`mr-2 ${question.isCorrect ? 'text-blue-500' : 'text-red-500'}`}>
                                            Q{question.SortNo || question.Qno}.
                                        </span>
                                        {question.Question}
                                    </h3>
                                </div>

                                {/* User Answer */}
                                <div className="mb-3 flex items-center gap-2 text-xs">
                                    <span className="text-gray-500">あなたの回答:</span>
                                    <span className={`font-bold ${question.isCorrect ? 'text-blue-600' : 'text-gray-700'
                                        }`}>
                                        {question.userAnswer || '未回答'}
                                    </span>
                                </div>

                                {/* Result */}
                                <div>
                                    {question.isCorrect ? (
                                        <div className="flex items-center gap-1 text-blue-500 font-bold text-xs">
                                            <i className="ri-check-circle-fill"></i>
                                            <span>正解</span>
                                        </div>
                                    ) : (
                                        <div className="space-y-2">
                                            <div className="flex items-center gap-1 text-red-500 font-bold text-xs">
                                                <i className="ri-close-circle-fill"></i>
                                                <span>不正解 (正解: {question.CollectAnswer})</span>
                                            </div>
                                            {question.Explanation && (
                                                <div className="mt-2 p-3 bg-white rounded-xl border border-gray-100">
                                                    <div className="text-xs font-bold text-orange-500 mb-1 flex items-center gap-1">
                                                        <i className="ri-lightbulb-line"></i>
                                                        解説
                                                    </div>
                                                    <div className="text-xs text-gray-600 leading-relaxed">
                                                        {question.Explanation}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}

                        {getChapterQuestions(activeTab).length === 0 && (
                            <div className="text-center py-8 text-gray-400 text-sm">
                                このチャプターには問題がありません
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
